import { ReactNode, useCallback, useRef, useState } from 'react'
import { cropImageToSquare, getDataTransferItems } from '~/helpers/fileUploadHelpers'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from '@hap/components'

interface Props {
  fileId?: string | null
  disabled?: boolean
  onValueChange: (file: File) => Promise<string>
  onRemove: () => void
  fallbackAvatar: ReactNode
}

/**
 * Avatar/Logo upload field component.
 *
 * Features:
 * - Click to select image file
 * - Automatic square cropping for avatars, original aspect for logos
 * - Optimistic preview while uploading
 * - Stores fileId in form for saving with other fields
 * - Different styling for avatars vs logos
 */
export function AvatarField(props: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // Determine if there's an avatar to remove
  const hasAvatar = !!(previewUrl || props.fileId)

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = getDataTransferItems(event)
      if (files.length === 0) return
      setLoading(true)
      const file = files[0]

      // Create a cropped preview for avatars
      try {
        const croppedFile = await cropImageToSquare(file, 512)
        const objectUrl = URL.createObjectURL(croppedFile)
        setPreviewUrl(objectUrl)
      } catch {
        // Fall back to original file preview if crop fails
        const objectUrl = URL.createObjectURL(file)
        setPreviewUrl(objectUrl)
      }

      try {
        // Upload file and get the fileId
        await props.onValueChange(file)
      } catch {
        // Clear preview on error
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl)
          setPreviewUrl(null)
        }
      }
      setLoading(false)
    },
    [props.onValueChange, previewUrl],
  )

  const handleChangeClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleRemove = useCallback(() => {
    // Clear preview if any
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    props.onRemove()
  }, [previewUrl])

  // Determine which avatar to display
  const renderAvatar = () => {
    // Show preview if we have one (during/after upload, before save)
    if (previewUrl) {
      return (
        <img
          src={previewUrl}
          alt="Avatar preview"
          className="w-full rounded-full object-cover"
        />
      )
    }

    return props.fallbackAvatar
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="bg-input-background size-21 group relative flex aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-lg p-3"
          disabled={loading || props.disabled}
        >
          {renderAvatar()}
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Icon name="arrows-clockwise" className="size-6 animate-spin text-white" />
            </div>
          ) : (
            <div className="absolute inset-0 flex scale-0 items-center justify-center bg-black/50 transition-transform group-hover:scale-100">
              <Icon name="pencil-simple" className="size-5 text-white" />
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem icon="pencil-simple" onClick={handleChangeClick}>
          Change image
        </DropdownMenuItem>
        {hasAvatar && (
          <DropdownMenuItem icon="x" onClick={handleRemove}>
            Remove image
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
        onChange={handleChange}
        className="hidden"
        disabled={loading || props.disabled}
      />
    </DropdownMenu>
  )
}
